/**
 * Sanity API utilities for fetching recommendation data
 *
 * Provides functions to fetch recommendation details from Sanity CMS
 * for the feedback labeling feature.
 *
 * Requirements: ES-REQ-011-SANITY to ES-REQ-016-SANITY
 */

import { client } from '@/service/sanity';
import type { RecommendationSnapshot, SanityFetchError, Antimicrobial } from '@/types/labeling';

/**
 * Treatment types matching main app structure
 */
interface TreatmentObject {
  titleDisplay: string | null;
  dose: number | null;
  frequency: number | null;
  duration: number | null;
  antimicrobialUnit: {
    antimicrobial: { title: string };
    doseUnit: { shorthand: string };
    form: { title: string };
  } | null;
}

/**
 * Merge treatment defaults with adjustments by index
 * Adapted from main app's utils/recommendation.ts
 * Now treatment1 returns TreatmentObject[] directly (not wrapped)
 */
const adjustTreatments = (
  defaults?: TreatmentObject[],
  adjustments?: TreatmentObject[]
): TreatmentObject[] => {
  if (!defaults) {
    return [];
  }

  if (!adjustments) {
    return defaults;
  }

  return defaults.map((defaultTreatment, index) => {
    const adjustment = adjustments[index];

    if (!adjustment) return defaultTreatment;

    // Simple object spread - complete overwrite for non-null values
    const overwrittenProperties = Object.fromEntries(
      Object.entries(adjustment).filter(([_, value]) => value !== null)
    );

    return {
      ...defaultTreatment,
      ...overwrittenProperties
    };
  });
};

/**
 * Fetch a recommendation by its ID from Sanity CMS
 *
 * Returns the recommendation data in a snapshot format suitable for
 * storage in the Labels tab Column D.
 *
 * @param recommendationId - The Sanity document ID (_id)
 * @returns Object with either data or error property
 */
export const fetchRecommendationById = async (recommendationId: string): Promise<{
  data?: RecommendationSnapshot;
  error?: SanityFetchError;
}> => {
  // Sanity GROQ query matching main app structure
  // Fetches treatment1 and treatmentAdjustments1 separately for proper merging
  const query = `*[_id == $recommendationId][0]{
    _id,
    displayTitle,
    "copy": pt::text(copy),
    "treatment1Text": pt::text(treatment1),
    "treatmentAdjustments1Text": pt::text(treatmentAdjustments1),
    treatmentLength,
    condition->{title},
    isAdult,
    isPrimary,
    "treatment1": treatment1[]->treatment{
      titleDisplay,
      dose,
      frequency,
      duration,
      antimicrobialUnit->{
        antimicrobial->{title},
        doseUnit->{shorthand},
        form->{title}
      }
    },
    "treatmentAdjustments1": treatmentAdjustments1[]{
      titleDisplay,
      dose,
      frequency,
      duration,
      antimicrobialUnit->{
        antimicrobial->{title},
        doseUnit->{shorthand},
        form->{title}
      }
    }
  }`;

  try {
    const result = await client.fetch(query, { recommendationId });

    // Handle case where recommendation is not found
    if (!result) {
      return {
        error: {
          type: 'missing',
          message: `Recommendation ${recommendationId} not found in Sanity`
        }
      };
    }

    // Build population description from available fields
    const populationParts = [];
    if (result.condition?.title) populationParts.push(`Condition: ${result.condition.title}`);
    if (result.isPrimary !== undefined) populationParts.push(result.isPrimary ? 'Primary care' : 'Secondary care');
    if (result.isAdult !== undefined) populationParts.push(result.isAdult ? 'Adults' : 'Children');
    const population = populationParts.join(' | ');

    // Build dosing information from treatment fields (plain text fallback)
    const dosingParts = [];
    if (result.treatment1Text) dosingParts.push(result.treatment1Text);
    if (result.treatmentAdjustments1Text) dosingParts.push(`Adjustments: ${result.treatmentAdjustments1Text}`);
    if (result.treatmentLength) dosingParts.push(`Duration: ${result.treatmentLength}`);
    const dosing = dosingParts.join(' | ');

    // Apply adjustTreatments logic to merge defaults with adjustments
    const mergedTreatments = adjustTreatments(result.treatment1, result.treatmentAdjustments1);

    // Convert to Antimicrobial format
    // Only filter out entries with no drug name (duration can be 0/null for single dose)
    const antimicrobials: Antimicrobial[] = mergedTreatments
      .filter((treatment: TreatmentObject) =>
        treatment.antimicrobialUnit?.antimicrobial?.title &&
        treatment.antimicrobialUnit?.antimicrobial?.title !== '0'
      )
      .map((treatment: TreatmentObject) => ({
        name: treatment.titleDisplay || treatment.antimicrobialUnit!.antimicrobial.title,
        dose: treatment.dose ?? undefined,
        dosageUnit: treatment.antimicrobialUnit?.doseUnit?.shorthand ?? undefined,
        frequency: treatment.frequency ?? undefined,
        duration: treatment.duration ?? undefined,
        durationUnit: 'days'
      }));

    // Return snapshot with mapped fields
    return {
      data: {
        _id: result._id,
        content: result.copy || result.displayTitle || '',
        dosing: dosing || '',
        population: population || '',
        fetchedAt: new Date().toISOString(),
        antimicrobials: antimicrobials.length > 0 ? antimicrobials : undefined
      }
    };
  } catch (error: any) {
    console.error('Sanity fetch error:', error);
    return {
      error: {
        type: 'api_error',
        message: error.message || 'Failed to fetch recommendation'
      }
    };
  }
};
